import { Phone, ShoppingBag, Layers } from "lucide-react";
import { CONTACT, IMAGES, BRANDS, LENSES } from "../data/site";
import Reveal from "../components/Reveal";
import Marquee from "../components/Marquee";
import { PageHero, CallToAction } from "../components/Sections";

const CARD_ICON_SIZE = 26;

const gallery = [
  { img: IMAGES.framesShelf, label: "Designer Collections" },
  { img: IMAGES.wallDisplay, label: "Tiffany & Co. Collection" },
  { img: IMAGES.sunglassesStore, label: "Premium Sunglasses" },
  { img: IMAGES.framesMacro, label: "Handcrafted Detail" },
  { img: IMAGES.stylishPatient, label: "Find Your Look" },
];

const GallerySection = () => (
  <section data-testid="frames-gallery" className="py-24 sm:py-28">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <Reveal>
        <div className="mb-12 max-w-2xl">
          <p className="text-tiffany uppercase tracking-[0.3em] text-xs font-semibold mb-4">
            The Collection
          </p>
          <h2 className="font-serif text-4xl sm:text-5xl font-semibold tracking-tight text-navy">
            Frames for every face & every style
          </h2>
        </div>
      </Reveal>
      <div className="grid gap-5 md:grid-cols-3 auto-rows-[300px]">
        {gallery.map((g, i) => (
          <Reveal
            key={g.label}
            delay={i * 0.06}
            className={i === 0 || i === 3 ? "md:col-span-2" : ""}
          >
            <div
              data-testid={`frame-tile-${i}`}
              className="group relative h-full overflow-hidden rounded-sm border border-border"
            >
              <img
                src={g.img}
                alt={g.label}
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/80 to-transparent" />
              <span className="absolute bottom-5 left-6 font-serif text-2xl font-semibold text-white">
                {g.label}
              </span>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const LensesSection = () => (
  <section data-testid="lenses-overview" className="bg-muted py-24 sm:py-28">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <Reveal>
        <div className="text-center mb-14 max-w-2xl mx-auto">
          <p className="text-tiffany uppercase tracking-[0.3em] text-xs font-semibold mb-4">
            Lens Options
          </p>
          <h2 className="font-serif text-4xl sm:text-5xl font-semibold tracking-tight text-navy">
            The right lenses, made right here
          </h2>
        </div>
      </Reveal>
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {LENSES.map((l, i) => (
          <Reveal key={l.title} delay={i * 0.06}>
            <div
              data-testid={`lens-card-${i}`}
              className="h-full bg-white border border-border rounded-sm p-7 hover:shadow-sm transition-shadow"
            >
              <Layers className="text-tiffany mb-5" size={CARD_ICON_SIZE} />
              <h3 className="font-serif text-xl font-semibold text-navy mb-2">{l.title}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{l.desc}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const OrderingCallout = () => (
  <section data-testid="online-ordering" className="py-24 sm:py-28">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <Reveal>
        <div className="relative overflow-hidden rounded-sm bg-navy text-white p-10 sm:p-16 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-sm bg-tiffany/20 mb-6">
              <ShoppingBag className="text-tiffany" size={28} />
            </div>
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold tracking-tight mb-4">
              Reorder contacts & eyewear with ease
            </h2>
            <p className="text-white/60 text-base leading-relaxed mb-8 max-w-lg">
              Need a refill on contact lenses or a second pair of your
              favorite frames? Call us to place an order — we'll have it
              ready for pickup or arrange delivery for current patients.
            </p>
            <a
              href={CONTACT.phoneHref}
              data-testid="ordering-call-button"
              className="inline-flex items-center gap-3 bg-tiffany hover:bg-tiffany-dark text-white px-7 py-4 rounded-sm font-semibold transition-colors active:scale-[0.98]"
            >
              <Phone size={18} /> Call to Order
            </a>
          </div>
          <img
            src={IMAGES.framesMacro}
            alt="Eyewear close-up"
            className="rounded-sm w-full h-72 object-cover"
          />
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Eyewear() {
  return (
    <div data-testid="eyewear-page">
      <PageHero
        eyebrow="Eyewear Boutique"
        title="Designer frames & premium lenses"
        subtitle="Browse a curated selection of the brands you love, fitted with high-quality lenses crafted in our own lab."
        image={IMAGES.framesShelf}
      />
      <GallerySection />
      <Marquee items={BRANDS} label="eyewear-brands" />
      <LensesSection />
      <OrderingCallout />
      <CallToAction
        title="Let's Find Your Perfect Pair"
        subtitle="Visit our boutique or give us a call — our opticians will help you choose frames and lenses you'll love."
      />
    </div>
  );
}
